import { ThingEventSnapshot } from '@eagle/core-data-types';
import { FaultUpdate } from '@eagle/data-function-types';
import { Stack } from '@ethings/ui-toolkit';
import { Typography } from '@mui/material';
import { omit } from 'lodash';
import { useMemo, useState, type FC } from 'react';
import { evaluate } from '../../evaluator';
import { useUiTemplate } from '../../hooks';
import { HISTORY_FAULT_CARD_DEFAULT_TEMPLATE } from '../../ui-templates/history-fault-card';
import { isSummarizedEvent } from '../map';
import { MiddleSpinner } from '../middle-spinner';
import { JourneyAccordion } from './journey-accordion';
import { JourneyFaultsDialog } from './journey-faults-dialog';
import { FaultDescription, NormalizedFaultEventItem } from './journey-faults.utils';

interface JourneyFaultsProps {
  faultEvents: ThingEventSnapshot[];
  startTime: Date;
  endTime: Date;
}

export const JourneyFaults: FC<JourneyFaultsProps> = ({ faultEvents, startTime, endTime }) => {
  const [expanded, setExpanded] = useState(true);
  const { template, loaded } = useUiTemplate('history-fault-card', HISTORY_FAULT_CARD_DEFAULT_TEMPLATE);

  const normalizedFaultEventItems: NormalizedFaultEventItem[] = useMemo(() => {
    return faultEvents.map((faultEvent) => {
      const originalEvents = isSummarizedEvent(faultEvent) ? faultEvent._originalEvents : [faultEvent];

      const sortedEvents = originalEvents.sort((a, b) => b.occurred.getTime() - a.occurred.getTime());
      const lastOccurredEvent = sortedEvents[0];
      const firstOccurredEvent = sortedEvents[sortedEvents.length - 1];
      const isOngoing = (lastOccurredEvent.data as unknown as FaultUpdate).active;

      return {
        event: omit(faultEvent, '_isSummarized', '_originalEvents', '_eventCount', '_summarizationMode'),
        originalEvents,
        firstEventOccurred: firstOccurredEvent.occurred,
        lastEventOccurred: lastOccurredEvent.occurred,
        isOngoing,
      };
    });
  }, [faultEvents]);

  return (
    <>
      <JourneyAccordion
        expanded={expanded}
        startTime={startTime}
        endTime={endTime}
        onChange={() => {
          setExpanded((prev) => !prev);
        }}
      >
        {loaded ? (
          <Stack spacing={2}>
            {normalizedFaultEventItems.map((item) => (
              <JourneyFaultItem key={item.event.eventId} item={item} template={template} />
            ))}
          </Stack>
        ) : (
          <MiddleSpinner />
        )}
      </JourneyAccordion>
    </>
  );
};

interface JourneyFaultItemProps {
  item: NormalizedFaultEventItem;
  template: object;
}

const JourneyFaultItem: FC<JourneyFaultItemProps> = ({ item, template }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const eventData = item.event.data as unknown as FaultUpdate;

  const faultDescription = (
    <Typography variant="body2" data-testid="fault-description">
      <FaultDescription thingId={item.event.thingId} eventData={eventData} />
    </Typography>
  );

  const openFaultGridDialog = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setIsDialogOpen((isDialogOpen) => !isDialogOpen);
  };

  const closeDialog = (): void => {
    setIsDialogOpen(false);
  };

  return (
    <>
      {evaluate(template, { item, faultDescription, openFaultGridDialog })}
      <JourneyFaultsDialog
        item={item}
        isOpen={isDialogOpen}
        onClose={closeDialog}
      />
    </>
  );
};
